import {Fragment, ReactNode, useLayoutEffect, useRef, useState} from "react";
import {PhoneHQTooltip} from "ch3-ui-lib";

interface OverflowChipsProps<T> {
    items: T[];
    /** Renders a single chip shown inline. */
    renderChip: (item: T, index: number) => ReactNode;
    /** Renders a single hidden item inside the "+N" hover tooltip. */
    renderTooltipItem: (item: T, index: number) => ReactNode;
    /** Tailwind classes for the "+N" overflow badge. */
    badgeClassName: string;
    /** Horizontal gap between chips, in px. Must match the `gap-*` class used. */
    gap?: number;
    className?: string;
}

/**
 * Renders a single, non-wrapping row of chips and collapses whatever doesn't
 * fit the available width into a trailing "+N" badge. Hovering the badge
 * reveals the hidden items in a tooltip.
 *
 * The number of visible chips is measured from the real rendered widths (via a
 * hidden mirror row) and recomputed whenever the container resizes, so it stays
 * correct across breakpoints and column-width changes.
 */
export default function OverflowChips<T>({
    items,
    renderChip,
    renderTooltipItem,
    badgeClassName,
    gap = 8,
    className,
}: OverflowChipsProps<T>) {
    const containerRef = useRef<HTMLDivElement>(null);
    const measureRef = useRef<HTMLDivElement>(null);
    const [visibleCount, setVisibleCount] = useState(items.length);

    useLayoutEffect(() => {
        const container = containerRef.current;
        const measure = measureRef.current;
        if (!container || !measure) return;

        const compute = () => {
            const avail = container.clientWidth;
            const widths = Array.from(measure.children).map(
                (c) => (c as HTMLElement).offsetWidth,
            );
            if (widths.length === 0) {
                setVisibleCount(0);
                return;
            }

            // Everything fits — no badge needed.
            const totalWidth =
                widths.reduce((a, b) => a + b, 0) + gap * (widths.length - 1);
            if (totalWidth <= avail) {
                setVisibleCount(widths.length);
                return;
            }

            // Reserve room for the "+N" badge (approximate; grows with digits).
            const badgeReserve = 30 + String(widths.length).length * 8 + gap;
            let used = 0;
            let count = 0;
            for (let i = 0; i < widths.length; i++) {
                const next = widths[i] + (i > 0 ? gap : 0);
                if (used + next + gap + badgeReserve <= avail) {
                    used += next;
                    count++;
                } else {
                    break;
                }
            }
            setVisibleCount(count);
        };

        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(container);
        return () => ro.disconnect();
    }, [items, gap]);

    if (items.length === 0) return null;

    const hidden = items.slice(visibleCount);

    return (
        <div ref={containerRef} className={className ?? "w-full"}>
            {/* Visible row */}
            <div
                className="flex items-center flex-nowrap overflow-hidden"
                style={{gap}}
            >
                {items.slice(0, visibleCount).map((item, i) => (
                    <Fragment key={i}>{renderChip(item, i)}</Fragment>
                ))}
                {hidden.length > 0 && (
                    <PhoneHQTooltip content={
                            <div className="flex flex-col gap-1">
                                {hidden.map((item, i) => (
                                    <Fragment key={i}>
                                        {renderTooltipItem(item, visibleCount + i)}
                                    </Fragment>
                                ))}
                            </div>
                        } align="center"><span className={badgeClassName + " cursor-pointer shrink-0"}>
                                +{hidden.length}
                            </span></PhoneHQTooltip>
                )}
            </div>

            {/* Hidden mirror row used only for measuring natural chip widths. */}
            <div
                ref={measureRef}
                aria-hidden
                className="absolute flex flex-nowrap invisible pointer-events-none"
                style={{gap, left: -99999, top: 0}}
            >
                {items.map((item, i) => (
                    <Fragment key={i}>{renderChip(item, i)}</Fragment>
                ))}
            </div>
        </div>
    );
}
